/**
 * Slow-moving sketch doodles behind a tile. Each theme has its own set of little glyphs that
 * drift and turn on their own timing, drawn in the tile's colour at low opacity so the content
 * on top stays crisp. Pure CSS animation (see .doodle in index.css); it stops under
 * prefers-reduced-motion and the app's a11y-reduced-motion class.
 */
export type TileTheme = "math" | "english" | "science" | "social" | "ability" | "courses" | "active" | "streak" | "xp" | "plan" | "tasks";

// Glyphs are 24x24, stroke = currentColor. Text glyphs use the app font.
const GLYPHS: Record<TileTheme, string[]> = {
  math: [
    '<path d="M12 5v14M5 12h14"/>', '<path d="M6 6l12 12M18 6L6 18"/>', '<path d="M5 9h14M5 15h14"/>',
    '<text x="12" y="17" font-size="15" font-weight="700" text-anchor="middle" fill="currentColor" stroke="none">7</text>',
    '<text x="12" y="17" font-size="15" font-weight="700" text-anchor="middle" fill="currentColor" stroke="none">½</text>',
    '<circle cx="12" cy="12" r="7"/>', '<path d="M4 18L12 5l8 13z"/>', '<path d="M5 12h6a3 3 0 0 0 0-6M11 12a3 3 0 0 1 0 6H5"/>',
  ],
  english: [
    '<text x="12" y="17" font-size="15" font-weight="700" text-anchor="middle" fill="currentColor" stroke="none">A</text>',
    '<text x="12" y="17" font-size="15" font-weight="700" text-anchor="middle" fill="currentColor" stroke="none">?</text>',
    '<text x="12" y="17" font-size="15" font-weight="700" text-anchor="middle" fill="currentColor" stroke="none">!</text>',
    '<path d="M7 10a2 2 0 1 1 3 2c-1 .7-1 2-1 3M17 10a2 2 0 1 1 3 2c-1 .7-1 2-1 3"/>',
    '<path d="M4 6h16M4 12h10M4 18h13"/>', '<path d="M5 19l11-11 3 3-11 11-4 1z"/>',
    '<text x="12" y="17" font-size="13" font-weight="700" text-anchor="middle" fill="currentColor" stroke="none">abc</text>',
  ],
  science: [
    '<circle cx="12" cy="12" r="2.5"/><ellipse cx="12" cy="12" rx="9" ry="3.5"/><ellipse cx="12" cy="12" rx="9" ry="3.5" transform="rotate(60 12 12)"/>',
    '<path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3"/>', '<circle cx="8" cy="9" r="3"/><circle cx="16" cy="15" r="2"/>',
    '<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>', '<path d="M4 16c3-6 7-6 8 0s5 6 8 0"/>', '<circle cx="12" cy="12" r="8"/><path d="M4 12h16M12 4c3 4 3 12 0 16"/>',
  ],
  social: [
    '<circle cx="12" cy="12" r="8"/><path d="M4 12h16M12 4c-3 4-3 12 0 16M12 4c3 4 3 12 0 16"/>',
    '<path d="M12 21s-6-5-6-11a6 6 0 0 1 12 0c0 6-6 11-6 11z"/><circle cx="12" cy="10" r="2"/>',
    '<path d="M5 21V4h11l-2 4 2 4H5"/>', '<path d="M4 20l4-9 4 5 3-7 5 11z"/>', '<circle cx="9" cy="9" r="3"/><circle cx="16" cy="11" r="2.5"/><path d="M3 20c0-4 3-6 6-6s6 2 6 6"/>',
    '<path d="M3 18h18M5 18V9l7-4 7 4v9M9 18v-5h6v5"/>',
  ],
  ability: [
    '<path d="M9 3h6v4a2 2 0 0 0 4 0h2v6h-4a2 2 0 0 0 0 4h4v4H9v-4a2 2 0 0 0-4 0H3v-6h4a2 2 0 0 0 0-4H3V3z"/>',
    '<path d="M4 12l3-5 3 10 3-8 3 6 4-3"/>', '<path d="M12 3a6 6 0 0 1 4 10.5V17H8v-3.5A6 6 0 0 1 12 3zM10 21h4"/>',
    '<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>', '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1"/>',
    '<path d="M6 8a3 3 0 0 1 6 0v9a3 3 0 0 1-6 0M12 8a3 3 0 0 1 6 0v9a3 3 0 0 1-6 0"/>',
  ],
  courses: ['<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>', '<path d="M12 4v16M4 12h16"/>', '<circle cx="12" cy="12" r="2"/>', '<path d="M12 3l1.5 4 4 1.5-4 1.5L12 14l-1.5-4-4-1.5 4-1.5z"/>'],
  active: ['<path d="M13 2L4 14h7l-1 8 9-12h-7z"/>', '<path d="M12 5v14M5 12h14"/>', '<circle cx="12" cy="12" r="6"/>', '<path d="M5 12l4 4 10-10"/>'],
  streak: ['<path d="M12 3c4 4 6 8 5 12a5 5 0 0 1-10 0c-1-4 1-8 5-12z"/>', '<path d="M12 9c2 2 3 4 2.5 6a2.5 2.5 0 0 1-5 0C9 13 10 11 12 9z"/>', '<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>'],
  xp: ['<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>', '<path d="M12 4v16M4 12h16"/>', '<text x="12" y="17" font-size="12" font-weight="800" text-anchor="middle" fill="currentColor" stroke="none">XP</text>', '<path d="M12 3l1.5 4 4 1.5-4 1.5L12 14l-1.5-4-4-1.5 4-1.5z"/>', '<path d="M4 18l5-9 4 6 3-4 4 7"/>'],
  tasks: [
    '<path d="M5 19l11-11 3 3-11 11-4 1z"/><path d="M14 10l3 3"/>',
    '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 12l3 3 5-6"/>',
    '<path d="M6 3h9l4 4v14H6z"/><path d="M9 12h7M9 16h5"/>',
    '<circle cx="12" cy="12" r="8"/><path d="M12 7v5l3 2"/>',
    '<text x="12" y="17" font-size="13" font-weight="800" text-anchor="middle" fill="currentColor" stroke="none">A+</text>',
    '<path d="M3 17l14-14 4 4L7 21H3z"/><path d="M7 13l2 2M10 10l2 2M13 7l2 2"/>',
    '<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>',
    '<path d="M4 6h16M4 12h16M4 18h10"/>',
  ],
  plan: ['<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/>', '<path d="M5 12l4 4 10-10"/>', '<path d="M4 6h16M4 12h10M4 18h13"/>', '<path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="8"/>', '<path d="M12 3l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1z"/>'],
};

// Fixed slots so the layout is stable across renders; each slot has its own timing.
const SLOTS = [
  { left: "6%", top: "58%", size: 22, dur: 13, delay: 0, rot: 10 },
  { left: "26%", top: "72%", size: 16, dur: 17, delay: -4, rot: -14 },
  { left: "44%", top: "60%", size: 20, dur: 15, delay: -9, rot: 8 },
  { left: "64%", top: "76%", size: 18, dur: 19, delay: -2, rot: -10 },
  { left: "82%", top: "58%", size: 24, dur: 14, delay: -7, rot: 12 },
  { left: "72%", top: "12%", size: 16, dur: 21, delay: -12, rot: -8 },
  { left: "90%", top: "30%", size: 14, dur: 16, delay: -5, rot: 16 },
  { left: "50%", top: "8%", size: 12, dur: 18, delay: -10, rot: -12 },
];

export function TileBackdrop({ theme, className = "", opacity = 0.16, count = 8 }: { theme: TileTheme; className?: string; opacity?: number; count?: number }) {
  const glyphs = GLYPHS[theme];
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none select-none ${className}`} aria-hidden="true" style={{ opacity }}>
      {SLOTS.slice(0, count).map((s, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          width={s.size}
          height={s.size}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="doodle absolute"
          style={{ left: s.left, top: s.top, ["--doodle-dur" as any]: `${s.dur}s`, ["--doodle-delay" as any]: `${s.delay}s`, ["--doodle-rot" as any]: `${s.rot}deg` }}
          dangerouslySetInnerHTML={{ __html: glyphs[i % glyphs.length] }}
        />
      ))}
    </div>
  );
}
